<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import CommentsModerationLayout from './CommentsModerationLayout.vue';
import CommentModerationHeader from './components/CommentModerationHeader.vue';
import CommentModerationCard from './components/CommentModerationCard.vue';
import CommentsCard from './components/CommentsCard.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const { t } = useI18n();

// Deleted post modal (remove from system)
const showRemovePostModal = ref(false);
const deletedPostInfo = ref(null);
const isRemovingPost = ref(false);

// Tab management
const selectedTab = ref('pending');
const tabKey = computed(() => selectedTab.value);

// Deleted comment confirmation dialog
const pendingDeletion = ref(null);
const deleteDialogRef = ref(null);

// Reply failed confirmation dialog (for deleted comments/posts)
const pendingReplyDeletion = ref(null);
const replyDeleteDialogRef = ref(null);

// Filtros e busca compartilhados entre todas as tabs
const sharedFilters = ref({});
const sharedSearchWord = ref('');

// Tab states (cada tab mantém apenas sua paginação e scroll)
const tabStates = ref({
  pending: {
    pagination: { perPage: 10, currentPage: 1 },
    scrollPosition: 0,
  },
  responded: {
    pagination: { perPage: 10, currentPage: 1 },
    scrollPosition: 0,
  },
  mentioned: {
    pagination: { perPage: 10, currentPage: 1 },
    scrollPosition: 0,
  },
});

// Current tab state
const currentTabState = computed(() => tabStates.value[tabKey.value]);

// Post detail panel
const showCommentCard = ref(false);
const postFilter = ref({});
const selectedComment = ref({});

// Computed properties from store
const currentComments = computed(() => {
  const key = tabKey.value;
  return store.getters['comments/getTabState'](key);
});

const tabCounts = computed(() => store.getters['comments/getTabCounts']);
const postInfo = computed(() => store.getters['comments/getPostInfo']);
const uiFlags = computed(() => store.getters['comments/getUIFlags']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const labels = computed(() => store.getters['labels/getLabels']);
const customClassifications = computed(
  () => store.getters['comments/getCustomClassifications']
);

const getBrands = computed(() => {
  const brands = inboxes.value.map(inbox => inbox);
  return brands.filter(
    (inbox, index, self) =>
      inbox.channel_type === 'Channel::FacebookPage' &&
      self.findIndex(item => item.id === inbox.id) === index
  );
});

const getPendingCount = computed(() => tabCounts.value.pending || 0);
const getRespondedCount = computed(() => tabCounts.value.responded || 0);
const getMencionedCount = computed(() => tabCounts.value.mentioned || 0);

// Helper Methods (defined first to avoid hoisting issues)
const formatFilterToRequest = filter => {
  return {
    sort: filter.sort?.key ?? 'desc',
    filter: filter.filter?.key ?? 'comment_date',
    classification: filter.classification?.map(item => item.key) ?? [],
    reply_by_ai: filter.reply_by_ai?.length > 0 ?? false,
    purchaseIntention:
      !!filter.purchaseIntention?.map(item => item.key)?.at(0) ?? false,
    brand: filter.brand?.map(item => item.key) ?? [],
    origin: filter.origin?.map(item => item.key) ?? [],
    platform: filter.platform?.map(item => item.key) ?? [],
    label: filter.label?.map(item => item.key) ?? [],
    customClassifications:
      filter.customClassifications?.map(item => item.key) ?? [],
    dateInterval: {
      start: filter.datetimeStart,
      end: filter.datetimeEnd,
    },
  };
};

const saveScrollPosition = () => {
  const scrollPosition = window.scrollY || window.pageYOffset;
  tabStates.value[tabKey.value].scrollPosition = scrollPosition;
};

const restoreScrollPosition = () => {
  const scrollPosition = tabStates.value[tabKey.value].scrollPosition;
  if (scrollPosition > 0) {
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        left: 0,
        behavior: 'instant',
      });
    }, 100);
  }
};

// Methods
const checkAndNavigateToPreviousPageIfEmpty = async (tab = null) => {
  const targetTab = tab || tabKey.value;
  const state = tabStates.value[targetTab];
  const tabState = store.getters['comments/getTabState'](targetTab);

  // Check if current page has no comments and navigate to previous page if needed
  if (
    tabState?.comments?.length === 0 &&
    tabState?.pagination?.currentPage > 1 &&
    tabState?.pagination?.totalPages > 0
  ) {
    // Current page is empty, go to previous page
    const previousPage = Math.max(1, tabState.pagination.currentPage - 1);
    state.pagination.currentPage = previousPage;

    const filtersToApply = formatFilterToRequest(sharedFilters.value);
    // Fetch comments for previous page
    await store.dispatch('comments/getTabComments', {
      tab: targetTab,
      filters: filtersToApply,
      commentText: sharedSearchWord.value,
      pagination: state.pagination,
    });
  }
};

const fetchTabComments = async (tab = null) => {
  const targetTab = tab || tabKey.value;
  const state = tabStates.value[targetTab];
  const filtersToApply = formatFilterToRequest(sharedFilters.value);

  await store.dispatch('comments/getTabComments', {
    tab: targetTab,
    filters: filtersToApply,
    commentText: sharedSearchWord.value,
    pagination: state.pagination,
  });

  // Check if we need to navigate to previous page after fetch
  await checkAndNavigateToPreviousPageIfEmpty(tab);
};

const MODERATION_TABS = ['pending', 'responded', 'mentioned'];

const refreshAllTabs = async () => {
  const filtersToApply = formatFilterToRequest(sharedFilters.value);
  // Sequential: getTabComments toggles global isFetching; parallel calls race that flag.
  await MODERATION_TABS.reduce(async (prev, tab) => {
    await prev;
    const state = tabStates.value[tab];
    await store.dispatch('comments/getTabComments', {
      tab,
      filters: filtersToApply,
      commentText: sharedSearchWord.value,
      pagination: state.pagination,
    });
    await checkAndNavigateToPreviousPageIfEmpty(tab);
  }, Promise.resolve());
};

const fetchPostComments = currentPage => {
  const filtersToApply = formatFilterToRequest(sharedFilters.value);
  return store.dispatch('comments/getPostComments', {
    pageId: selectedComment.value.facebook_page.id,
    postId: selectedComment.value.post.id,
    currentPage: currentPage,
    order: filtersToApply.sort,
    classification: filtersToApply.classification,
    commentText: undefined,
  });
};

const fetchPostInfo = (pageId, postId) => {
  const page = pageId ?? selectedComment.value?.facebook_page?.id;
  const post = postId ?? selectedComment.value?.post?.id;
  return store.dispatch('comments/getPostInfo', { pageId: page, postId: post });
};

const openDeletedPostModal = ({ postId, pageId }) => {
  deletedPostInfo.value = { postId, pageId };
  showRemovePostModal.value = true;
};

const closeRemovePostModal = () => {
  showRemovePostModal.value = false;
  deletedPostInfo.value = null;
};

const toggleShowCommentCard = async (status, comment) => {
  if (status && comment) {
    try {
      await fetchPostInfo(comment.facebook_page.id, comment.post.id);
    } catch (e) {
      if (e.deletedPost) {
        openDeletedPostModal(e.deletedPost);
        return;
      }
      throw e;
    }

    const savedScrollPosition = window.scrollY || window.pageYOffset;

    showCommentCard.value = status;
    selectedComment.value = comment;
    postFilter.value = {};

    fetchPostComments(1);

    const restoreScroll = () => {
      window.scrollTo({
        top: savedScrollPosition,
        left: 0,
        behavior: 'instant',
      });
    };

    restoreScroll();
  } else {
    showCommentCard.value = status;
    postFilter.value = {};
    selectedComment.value = {};
  }
};

const confirmRemovePost = async () => {
  if (!deletedPostInfo.value?.postId || !deletedPostInfo.value?.pageId) return;
  isRemovingPost.value = true;
  try {
    await store.dispatch('posts/deletePost', {
      pageId: deletedPostInfo.value.pageId,
      postId: deletedPostInfo.value.postId,
    });
    closeRemovePostModal();
    toggleShowCommentCard(false);
    fetchTabComments();
  } finally {
    isRemovingPost.value = false;
  }
};

const handleCommentSelect = comment => {
  toggleShowCommentCard(true, comment);
};

const handlePostLoadMore = () => {
  fetchPostComments(currentTabState.value.pagination.currentPage + 1);
};

const handleResponded = commentId => {
  // When a reply is created, avoid removing the comment from the 'responded' tab.
  // Only remove from 'pending' and 'mentioned' tabs so the comment stays visible
  // in 'responded' (where the user currently expects to see it immediately).
  ['pending', 'mentioned'].forEach(tab => {
    const state = store.getters['comments/getTabState'](tab);
    if (state.comments) {
      state.comments = state.comments.filter(
        comment => comment.id !== commentId
      );
    }
  });
};

const handleReplyFailed = ({
  commentId,
  deletedCommentId,
  error,
  isMentioned,
}) => {
  // Show confirmation dialog when reply fails due to deleted comment/post
  pendingReplyDeletion.value = {
    commentId: deletedCommentId || commentId,
    error,
    isMentioned: isMentioned || false,
  };
  // Use nextTick to ensure the ref is available and DOM is updated
  nextTick(() => {
    if (replyDeleteDialogRef.value) {
      replyDeleteDialogRef.value.open();
    }
  });
};

const handleCheckCommentDeleted = eventData => {
  const {
    commentId,
    action,
    deletedCommentId,
    error,
    isMentioned,
    pageId,
    postId,
  } = eventData || {};

  // Show confirmation dialog when comment is deleted before action
  if (action === 'reply') {
    pendingReplyDeletion.value = {
      commentId: deletedCommentId || commentId,
      error,
      isMentioned: isMentioned || false,
      pageId,
      postId,
    };
    nextTick(() => {
      if (replyDeleteDialogRef.value) {
        replyDeleteDialogRef.value.open();
      }
    });
  } else {
    // For hide or delete actions
    pendingDeletion.value = {
      commentId: deletedCommentId || commentId,
      error,
      action: action || 'delete', // 'hide' or 'delete'
      pageId,
      postId,
    };
    nextTick(() => {
      if (deleteDialogRef.value) {
        deleteDialogRef.value.open();
      }
    });
  }
};

const confirmReplyDeleteFromPlatform = async () => {
  if (pendingReplyDeletion.value) {
    const { commentId, pageId, postId } = pendingReplyDeletion.value;
    const commentIdNum = Number(commentId);

    // Delete from database via API (comment already deleted on Instagram, just remove from our DB)
    if (pageId && postId) {
      try {
        await store.dispatch('comments/delete', {
          commentId: commentIdNum,
          pageId,
          postId,
          callback: async ({ success }) => {
            if (success) {
              // Refresh tab counts from backend to update footer numbers
              await fetchTabComments(selectedTab.value);
              // Check if we need to navigate to previous page after deletion
              await checkAndNavigateToPreviousPageIfEmpty();
            }
          },
        });
      } catch (error) {
        // Even if API fails (e.g., comment already deleted), remove from UI
        store.commit('comments/DELETE_POST_COMMENT', commentIdNum);
        // Refresh tab counts and check if we need to navigate to previous page
        await fetchTabComments(selectedTab.value);
        await checkAndNavigateToPreviousPageIfEmpty();
      }
    } else {
      // Fallback: just remove from store if we don't have pageId/postId
      store.commit('comments/DELETE_POST_COMMENT', commentIdNum);
      // Refresh tab counts and check if we need to navigate to previous page
      await fetchTabComments(selectedTab.value);
      await checkAndNavigateToPreviousPageIfEmpty();
    }

    pendingReplyDeletion.value = null;
  }
  // Close dialog after confirming
  replyDeleteDialogRef.value?.close();
};

const cancelReplyDeleteFromPlatform = () => {
  // Just clean up state - dialog is already closing from the cancel button
  pendingReplyDeletion.value = null;
};

const onTogglePostActive = () => {
  postInfo.value.active = !postInfo.value.active;
};

const onFilterPostChange = filter => {
  sharedFilters.value = filter;
  fetchPostComments(1);
};

const onFilterHeaderChange = filter => {
  // Salvar scroll position antes de atualizar
  saveScrollPosition();

  // Atualizar filtros compartilhados
  sharedFilters.value = filter;

  // Resetar paginação de todas as tabs e limpar cache
  Object.keys(tabStates.value).forEach(tab => {
    tabStates.value[tab].pagination.currentPage = 1;
    // Limpar comentários do store para forçar reload
    store.commit('comments/SET_TAB_COMMENTS', {
      tab,
      data: {
        comments: [],
        current_page: 1,
        total_count: 0,
        total_pages: 0,
        per_page: 10,
      },
    });
  });

  // Buscar dados da tab atual com novos filtros
  fetchTabComments();
};

const onSearch = searchWord => {
  // Salvar scroll position antes de atualizar
  saveScrollPosition();

  // Atualizar busca compartilhada
  sharedSearchWord.value = searchWord;

  // Resetar paginação de todas as tabs e limpar cache
  Object.keys(tabStates.value).forEach(tab => {
    tabStates.value[tab].pagination.currentPage = 1;
    // Limpar comentários do store para forçar reload
    store.commit('comments/SET_TAB_COMMENTS', {
      tab,
      data: {
        comments: [],
        current_page: 1,
        total_count: 0,
        total_pages: 0,
        per_page: 10,
      },
    });
  });

  // Buscar dados da tab atual com nova busca
  fetchTabComments();
};

const handleDeleted = ({
  commentId,
  replyId = null,
  deleted,
  deletedCommentId,
  error,
}) => {
  // If comment was deleted on platform, show confirmation dialog
  if (deleted && deletedCommentId) {
    pendingDeletion.value = {
      commentId: replyId || commentId,
      replyId: replyId || null,
      error,
    };
    deleteDialogRef.value?.open();
    return;
  }

  // Normal deletion - remove immediately
  if (replyId) {
    store.commit('comments/DELETE_POST_COMMENT', replyId);
  } else {
    store.commit('comments/DELETE_POST_COMMENT', commentId);
  }

  // Check if we need to navigate to previous page after deletion
  nextTick(async () => {
    await checkAndNavigateToPreviousPageIfEmpty();
  });
};

const confirmDeleteFromPlatform = async () => {
  if (pendingDeletion.value) {
    const { commentId, replyId, pageId, postId } = pendingDeletion.value;
    const targetId = Number(replyId || commentId);

    // Delete from database via API (comment already deleted on Instagram, just remove from our DB)
    if (pageId && postId) {
      try {
        await store.dispatch('comments/delete', {
          commentId: targetId,
          pageId,
          postId,
          callback: async ({ success }) => {
            if (success) {
              // Refresh tab counts from backend to update footer numbers
              await fetchTabComments(selectedTab.value);
              // Check if we need to navigate to previous page after deletion
              await checkAndNavigateToPreviousPageIfEmpty();
            }
          },
        });
      } catch (error) {
        // Even if API fails (e.g., comment already deleted), remove from UI
        store.commit('comments/DELETE_POST_COMMENT', targetId);
        // Refresh tab counts and check if we need to navigate to previous page
        await fetchTabComments(selectedTab.value);
        await checkAndNavigateToPreviousPageIfEmpty();
      }
    } else {
      // Fallback: just remove from store if we don't have pageId/postId
      store.commit('comments/DELETE_POST_COMMENT', targetId);
      // Refresh tab counts and check if we need to navigate to previous page
      await fetchTabComments(selectedTab.value);
      await checkAndNavigateToPreviousPageIfEmpty();
    }

    pendingDeletion.value = null;
  }
  // Close dialog after confirming
  deleteDialogRef.value?.close();
};

const cancelDeleteFromPlatform = () => {
  // User chose to keep the comment - just clean up state
  pendingDeletion.value = null;
};

// Computed properties for delete confirmation dialog
const deleteDialogTitle = computed(() => {
  return pendingDeletion.value?.action === 'hide'
    ? t('COMMENTS.MODERATION.HIDE_CONFIRMATION.TITLE')
    : t('COMMENTS.MODERATION.DELETE_CONFIRMATION.TITLE');
});

const deleteDialogDescription = computed(() => {
  return pendingDeletion.value?.action === 'hide'
    ? t('COMMENTS.MODERATION.HIDE_CONFIRMATION.DESCRIPTION')
    : t('COMMENTS.MODERATION.DELETE_CONFIRMATION.DESCRIPTION');
});

const deleteDialogConfirmLabel = computed(() => {
  return pendingDeletion.value?.action === 'hide'
    ? t('COMMENTS.MODERATION.HIDE_CONFIRMATION.CONFIRM')
    : t('COMMENTS.MODERATION.DELETE_CONFIRMATION.CONFIRM');
});

const handleHidden = ({
  commentId,
  replyId = null,
  deleted,
  deletedCommentId,
  error,
}) => {
  // If comment was deleted on platform, show confirmation dialog
  if (deleted && deletedCommentId) {
    pendingDeletion.value = {
      commentId: replyId || commentId,
      replyId: replyId || null,
      error,
    };
    deleteDialogRef.value?.open();
  }
};

const onTabChange = tab => {
  // Salvar scroll position da tab atual antes de mudar
  saveScrollPosition();

  // Mapear nome da tab para chave
  const tabMap = {
    Pendentes: 'pending',
    Respondidos: 'responded',
    Mencionados: 'mentioned',
  };
  const newTabKey = tabMap[tab] || 'pending';

  // Mudar para nova tab
  selectedTab.value = newTabKey;

  // Fechar painel de detalhes ao mudar de tab
  showCommentCard.value = false;
  selectedComment.value = {};

  // Buscar dados da nova tab se ainda não foram carregados
  const newTabState = store.getters['comments/getTabState'](newTabKey);
  const tabLoaded = store.state.comments?.tabLoaded?.[newTabKey];

  // Se a tab ainda não foi carregada a partir do servidor (tabLoaded === false)
  // ou não possui comentários locais, devemos buscar a tab para popular a lista
  // completa. Caso contrário apenas restauramos a posição de scroll.
  if (
    !tabLoaded ||
    !newTabState.comments ||
    newTabState.comments.length === 0
  ) {
    fetchTabComments(newTabKey);
  } else {
    // Restaurar scroll position da nova tab
    restoreScrollPosition();
  }
};

const onPageChange = page => {
  // Salvar scroll position antes de mudar página
  saveScrollPosition();

  currentTabState.value.pagination.currentPage = page;
  fetchTabComments();

  // Scroll para o topo após mudança de página
  setTimeout(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, 100);
};

// Lifecycle hooks
onMounted(() => {
  // Buscar dados iniciais
  fetchTabComments('pending');
  store.dispatch('labels/get');
  store.dispatch('comments/getCustomClassifications');
});

onBeforeUnmount(() => {
  // Salvar scroll position ao desmontar
  saveScrollPosition();
});

// Watch for scroll events to save position
let scrollTimeout = null;
const handleScroll = () => {
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }
  scrollTimeout = setTimeout(() => {
    saveScrollPosition();
  }, 150);
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }
});
</script>

<template>
  <CommentsModerationLayout
    :is-loading="uiFlags.isFetching && !showCommentCard"
    loading-message="Carregando comentários..."
    :no-records-found="!currentComments.comments.length && !uiFlags.isFetching"
    no-records-message="Nenhum comentário encontrado"
  >
    <template #header>
      <CommentModerationHeader
        :tabs="[
          { name: 'Pendentes', count: getPendingCount },
          { name: 'Respondidos', count: getRespondedCount },
          { name: 'Mencionados', count: getMencionedCount },
        ]"
        :header-title="
          selectedTab === 'pending'
            ? 'Pendentes'
            : selectedTab === 'responded'
              ? 'Respondidos'
              : 'Mencionados'
        "
        feature-name="facebook-page-pending-posts-comments"
        :initial-tab="
          selectedTab === 'pending'
            ? 'Pendentes'
            : selectedTab === 'responded'
              ? 'Respondidos'
              : 'Mencionados'
        "
        :brands="getBrands"
        :labels="labels"
        :custom-classifications="customClassifications"
        :initial-filter-values="sharedFilters"
        :list-loading="uiFlags.isFetching"
        @tab-change="onTabChange"
        @filter="onFilterHeaderChange"
        @search="onSearch"
        @refresh="refreshAllTabs"
      />
    </template>

    <template #body>
      <div
        class="flex flex-col lg:flex-row w-full gap-4 sm:gap-6 px-4 sm:px-6 lg:px-8 py-6 prevent-scroll-anchor"
      >
        <!-- Comments List -->
        <div
          class="flex flex-col w-full transition-all duration-300 ease-in-out"
          :class="{
            'lg:w-full': !showCommentCard,
            'lg:w-5/12 xl:w-1/2': showCommentCard,
          }"
        >
          <div class="space-y-4 sm:space-y-5">
            <div
              v-for="comment in currentComments.comments"
              :key="comment.id"
              :data-comment-id="comment.id"
            >
              <CommentModerationCard
                class="hover:cursor-pointer transition-all duration-200 scroll-mt-20"
                :class="{
                  'animate-pulse-subtle':
                    selectedComment.id === comment.id && showCommentCard,
                }"
                :comment="comment"
                :selected="selectedComment.id === comment.id"
                :ui-flags="uiFlags"
                @select="handleCommentSelect"
                @responded="handleResponded"
                @reply-failed="handleReplyFailed"
                @check-comment-deleted="handleCheckCommentDeleted"
                @hidden="handleHidden"
                @deleted="handleDeleted"
              />
            </div>
          </div>

          <div class="sticky bottom-0 mt-auto pt-4 [&_>_div]:before:!hidden">
            <PaginationFooter
              v-if="currentComments.pagination.totalPages > 1"
              :current-page="currentTabState.pagination.currentPage"
              :total-items="currentComments.pagination.totalCount"
              :items-per-page="currentTabState.pagination.perPage"
              @update:current-page="onPageChange"
            />
          </div>
        </div>

        <!-- Post Detail Panel - Desktop -->
        <div
          v-if="showCommentCard"
          class="hidden lg:block w-full lg:w-7/12 xl:w-1/2 sticky top-4 h-fit transition-all duration-300 ease-in-out animate-slide-in-right"
        >
          <div
            class="bg-white dark:bg-n-solid-2 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
          >
            <div
              class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700"
            >
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ $t('COMMENTS.MODERATION.POST_DETAILS') }}
              </h3>
              <button
                type="button"
                class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                @click="toggleShowCommentCard(false)"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="text-slate-600 dark:text-slate-400"
                >
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div class="max-h-[calc(100vh-120px)] overflow-y-auto">
              <CommentsCard
                :id="selectedComment.id"
                :key="selectedComment.id"
                :post-info="postInfo"
                :initial-filter-values="postFilter"
                :page-id="selectedComment.facebook_page.id"
                :post-id="selectedComment.post.id"
                :selected-comment="selectedComment"
                @load-more="handlePostLoadMore"
                @toggle-active="onTogglePostActive"
                @filter="onFilterPostChange"
                @responded="handleResponded"
                @reply-failed="handleReplyFailed"
                @deleted="handleDeleted"
                @hidden="handleHidden"
              />
            </div>
          </div>
        </div>

        <!-- Post Detail Panel - Mobile (Modal) -->
        <woot-modal
          v-if="showCommentCard"
          :show="showCommentCard"
          class="lg:hidden"
          @close="toggleShowCommentCard(false)"
        >
          <div
            class="bg-white dark:bg-n-solid-2 rounded-t-xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div
              class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0"
            >
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ $t('COMMENTS.MODERATION.POST_DETAILS') }}
              </h3>
              <button
                type="button"
                class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                @click="toggleShowCommentCard(false)"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="text-slate-600 dark:text-slate-400"
                >
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div class="overflow-y-auto flex-1">
              <CommentsCard
                :id="selectedComment.id"
                :key="selectedComment.id"
                :post-info="postInfo"
                :initial-filter-values="postFilter"
                :page-id="selectedComment.facebook_page.id"
                :post-id="selectedComment.post.id"
                :selected-comment="selectedComment"
                @load-more="handlePostLoadMore"
                @toggle-active="onTogglePostActive"
                @filter="onFilterPostChange"
                @responded="handleResponded"
                @reply-failed="handleReplyFailed"
                @check-comment-deleted="handleCheckCommentDeleted"
                @deleted="handleDeleted"
                @hidden="handleHidden"
              />
            </div>
          </div>
        </woot-modal>

        <!-- Deleted post: confirm remove from system -->
        <woot-modal
          v-model:show="showRemovePostModal"
          :on-close="closeRemovePostModal"
        >
          <div class="flex flex-col gap-4 p-4">
            <h3 class="text-base font-semibold text-slate-900 dark:text-white">
              {{ t('COMMENTS.POST_DETAILS.DELETED_POST_TITLE') }}
            </h3>
            <p class="text-sm text-slate-600 dark:text-slate-400">
              {{ t('COMMENTS.POST_DETAILS.DELETED_POST_MESSAGE') }}
            </p>
            <div class="flex justify-end gap-2 mt-2">
              <button
                type="button"
                class="woot-button secondary"
                :disabled="isRemovingPost"
                @click="closeRemovePostModal"
              >
                {{ t('COMMENTS.POST_DETAILS.DELETED_POST_CANCEL') }}
              </button>
              <button
                type="button"
                class="woot-button danger"
                :disabled="isRemovingPost"
                @click="confirmRemovePost"
              >
                {{ t('COMMENTS.POST_DETAILS.REMOVE_POST') }}
              </button>
            </div>
          </div>
        </woot-modal>
      </div>
    </template>
  </CommentsModerationLayout>

  <!-- Deleted Comment Confirmation Dialog -->
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="deleteDialogTitle"
    :description="deleteDialogDescription"
    :confirm-button-label="deleteDialogConfirmLabel"
    show-cancel-button
    :cancel-button-label="
      pendingDeletion?.action === 'hide'
        ? t('COMMENTS.MODERATION.HIDE_CONFIRMATION.CANCEL')
        : t('COMMENTS.MODERATION.DELETE_CONFIRMATION.CANCEL')
    "
    @confirm="confirmDeleteFromPlatform"
    @close="cancelDeleteFromPlatform"
  />

  <!-- Reply Failed - Deleted Comment/Post Confirmation Dialog -->
  <Dialog
    ref="replyDeleteDialogRef"
    type="alert"
    :title="
      pendingReplyDeletion?.isMentioned
        ? t('COMMENTS.MODERATION.REPLY_FAILED_MENTIONED.TITLE')
        : t('COMMENTS.MODERATION.REPLY_FAILED_OWNED.TITLE')
    "
    :description="
      pendingReplyDeletion?.isMentioned
        ? t('COMMENTS.MODERATION.REPLY_FAILED_MENTIONED.DESCRIPTION')
        : t('COMMENTS.MODERATION.REPLY_FAILED_OWNED.DESCRIPTION')
    "
    :confirm-button-label="t('COMMENTS.MODERATION.REPLY_FAILED.CONFIRM')"
    :cancel-button-label="t('COMMENTS.MODERATION.REPLY_FAILED.CANCEL')"
    @confirm="confirmReplyDeleteFromPlatform"
    @close="cancelReplyDeleteFromPlatform"
  />
</template>

<style scoped>
.prevent-scroll-anchor {
  overflow-anchor: none;
}

@keyframes slide-in-right {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-in-right {
  animation: slide-in-right 0.3s ease-out;
}

.animate-pulse-subtle {
  animation: pulse-subtle 0.5s ease-in-out;
}

@keyframes pulse-subtle {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.95;
  }
}

.scroll-mt-20 {
  scroll-margin-top: 5rem;
}
</style>
